/**
 * EmptyState — icon + title + description + optional CTA button
 *
 * @example
 * <EmptyState
 *   icon={<Users size={40} />}
 *   title={t('admin.no_users_title')}
 *   description={t('admin.no_users_desc')}
 *   action={{ label: t('admin.add_user'), onClick: () => setOpen(true) }}
 * />
 */

import PropTypes from 'prop-types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InboxIcon } from 'lucide-react';

export default function EmptyState({ icon, title, description, action, className }) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-10',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className="mb-4 text-[var(--border-heavy)]">
        {icon || <InboxIcon size={40} strokeWidth={1.5} />}
      </div>
      {title && (
        <h3 className="font-display text-lg font-bold text-[var(--text-main)] mb-1">
          {title}
        </h3>
      )}
      {description && (
        <p className="text-sm text-[var(--text-muted)] max-w-xs">{description}</p>
      )}
      {action && (
        <Button
          className="mt-4 btn-primary"
          onClick={action.onClick}
          aria-label={action.label}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

EmptyState.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string,
  description: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  className: PropTypes.string,
};
